import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus } from "react-bootstrap-icons";
import { Edit2 } from "lucide-react";
import { toast } from "react-toastify";
import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import api from "../../../../Axios.js";
import { defaultDateFormatter } from "../../../../Helper/functionHelper.js";
import { Autocomplete, TextField } from "@mui/material";
import {
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import CommonFilter from "../../../../components/common/CommonFilter.jsx";
import EmployeeSelector from "./EmployeeSelector.jsx";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper.jsx";
import useModal from "../../../../hooks/useModal.js";
import AssignedEmployeeList from "./AssignedEmployeeList.jsx";
import useLoader from "../../../../hooks/useLoader.js";

const INITIAL_FORM = {
  id: null,
  category_id: "",
  program_id: "",
  start_date: null,
  end_date: null,
  employee_ids: [],
};

const ProgramAssignList = () => {

  const { showModal } = useModal();
  const { hideLoader, showLoader } = useLoader();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(20);

  const [modalVisible, setModalVisible] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const [employees, setEmployees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [documentPath, setDocumentPath] = useState("");
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    start_date: null,
    end_date: null,
    employee_id: 411
  });

  const filterConfig = useMemo(() => [
    { name: "search", type: "text", label: "Program Name / Code", grid: 3 },
    { name: "start_date", type: "date", label: "Start Date", grid: 3 },
    { name: "end_date", type: "date", label: "End Date", grid: 3 },
  ], []);

  const fetchAssignments = useCallback(async (page = currentPage, customFilters = filters) => {
    try {
      showLoader();
      const res = await api.post("/lnd/get-assign-program-list", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });
      setAssignments(res.data?.data?.data || []);
      const pagination = res.data.data.pagination;
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.totalRecords);
      setDocumentPath(res.data?.data?.documentPath);
    } catch {
      toast.error("Failed to fetch assignments");
    } finally {
      hideLoader();
    }
  }, [currentPage, itemsPerPage]);

  const fetchPrograms = useCallback(async (categoryId = "") => {
    try {
      const res = await api.get(`/lnd/get-program-list-dropdown?category_id=${categoryId}`);
      setPrograms(res.data?.data?.data || []);
    } catch {
      toast.error("Failed to load programs");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("/lnd/get-category-dropdown");
      setCategories(res.data.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load categories");
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get("/lnd/get-user-list");
      setEmployees(res.data?.data?.data || []);
    } catch {
      toast.error("Failed to load employees");
    }
  }, []);

  useEffect(() => { fetchAssignments(currentPage, filters); }, [currentPage, filters]);

  useEffect(() => {
    fetchPrograms();
    fetchEmployees();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchPrograms(form.category_id);
  }, [form.category_id]);

  const handleFormChange = useCallback(
    (field) => (e) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value })),
    []
  );

  const handleDateChange = useCallback(
    (field) => (value) =>
      setForm((prev) => ({ ...prev, [field]: value })),
    []
  );

  const viewAssignedEmployees = (key, title, assignedId, endDate) => {
    let body;

    if (key === "employees") {
      body = (
        <div className="text-center">
          <AssignedEmployeeList assignedId={assignedId} endDate={endDate} documentPath={documentPath} />
        </div>
      );
    }

    showModal(title, body, "md");
  };

  const handleEmployeeChange = useCallback(
    (ids) => {
      setForm((prev) => ({ ...prev, employee_ids: ids }));
      if (ids.length > 0) setErrors((prev) => ({ ...prev, employee_ids: "" }));
    },
    []
  );

  const openAddModal = useCallback(() => {
    setForm(INITIAL_FORM);
    setErrors({});
    setModalVisible(true);
  }, []);

  const openEditModal = useCallback(async (id) => {
    try {
      const res = await api.get(`/lnd/assign-program/${id}`);
      let data = res?.data?.data?.assignment;

      // parse employee_ids
      let empIds = data.employee_ids || [];
      if (typeof empIds === "string") {
        data.employee_ids = JSON.parse(empIds);
      }

      // ✅ convert dates
      data.start_date = data.start_date ? new Date(data.start_date) : null;
      data.end_date = data.end_date ? new Date(data.end_date) : null;

      setForm(data);
      setErrors({});
      setModalVisible(true);
    } catch {
      toast.error("Failed to load assignment details");
    }
  }, []);

  const openViewEmployees = useCallback((row) => {
    let empIds = row.employee_ids;
    if (typeof empIds === "string") empIds = JSON.parse(empIds);

    setSelectedEmployees(
      employees.filter((emp) => empIds.includes(emp._UserId))
    );
    setViewModal(true);
  }, [employees]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.program_id) newErrors.program_id = "Program is required";
    if (form.employee_ids.length === 0) newErrors.employee_ids = "At least one employee must be selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    try {
      const payload = {
        program_id: form.program_id,
        start_date: form.start_date
          ? dayjs(form.start_date).format("YYYY-MM-DD")
          : null,
        end_date: form.end_date
          ? dayjs(form.end_date).format("YYYY-MM-DD")
          : null,
        employee_ids: form.employee_ids,
      };

      if (form.id) {
        await api.put(`/lnd/assign-program/${form.id}`, payload);
        toast.success("Updated successfully");
      } else {
        await api.post("/lnd/assign", payload);
        toast.success("Assigned successfully");
      }
      setModalVisible(false);
      fetchAssignments(currentPage, filters);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Save failed");
    }
    finally {
      setLoading(false);
    }
  };

  const handleExport = async (filters) => {
    try {
      const res = await api.post(
        "/lnd/export-assigned-program-list",
        { searchData: filters },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "assigned_program_export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const columns = useMemo(() => [
    { label: "Program Code", field: "program.program_code" },
    { label: "Program Name", field: "program.program_name" },
    {
      label: "Start Date",
      render: (row) => defaultDateFormatter(row.start_date) || "N/A",
    },
    {
      label: "End Date",
      render: (row) => defaultDateFormatter(row.end_date) || "N/A",
    },
    {
      label: "Employees Assigned",
      render: (row) => {
        let empIds = row.employee_ids || [];
        if (typeof empIds === "string") {
          try {
            empIds = JSON.parse(empIds);
          } catch {
            empIds = [];
          }
        }
        return (
          <div className="text-primary cursor-pointer" onClick={() => { viewAssignedEmployees("employees", "Assigned Employees", row.id, row.end_date) }}>
            {(Array.isArray(empIds) ? empIds : []).length}
          </div>
        );
      },
    },
    {
      label: "Actions",
      render: (row) => (
        <div className="d-flex gap-2">
          <div className="cursor-pointer text-danger" onClick={() => openEditModal(row.id)}>
            <Edit2 size={12} /> Edit
          </div>
        </div>
      ),
    },
  ], [openViewEmployees, openEditModal]);

  return (
    <div className="container-fluid py-4">

      {/* ── Header / Filter ── */}
      <div className="row g-2 align-items-end mb-4">
        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{ search: "", start_date: null, end_date: null }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchAssignments(1, data)
            }}
          // onExport={handleExport}
          />
        </div>
        <div className="col-12 col-lg-auto d-flex justify-content-end">
          <button
            onClick={openAddModal}
            className="btn btn-success d-flex align-items-center gap-1 px-3 py-2 w-100 w-lg-auto"
          >
            <Plus size={14} />
            <span className="d-none d-sm-inline">Assign Program</span>
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <DynamicTable
        columns={columns}
        data={assignments}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={setCurrentPage}
        rowKey="id"
        loading={loading}
      />

      {modalVisible && (
        <ModalWrapper onClose={() => setModalVisible(false)} width="modal-lg">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0 text-dark">
              {form.id ? "Edit Assignment" : "Assign Program"}
            </h5>
            <IconButton size="small" onClick={() => setModalVisible(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* 🔥 FORM GRID */}
          <div className="row g-3">

            {/* Category */}
            <div className="col-12 col-md-6">
              <Autocomplete
                options={categories || []}
                getOptionLabel={(option) => option.title || ""}
                value={
                  categories.find((cat) => cat.id === form.category_id) || null
                }
                onChange={(e, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    category_id: newValue?.id || "",
                  }));

                  if (errors.category_id) {
                    setErrors((prev) => ({ ...prev, category_id: "" }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Category"
                    size="small"
                    error={!!errors.category_id}
                    helperText={errors.category_id}
                  />
                )}
              />
            </div>

            {/* Program */}
            <div className="col-12 col-md-6">
              <Autocomplete
                options={programs || []}
                getOptionLabel={(option) => option.program_name || ""}

                // 🔥 important (map id → object)
                value={
                  programs.find((p) => p.id === form.program_id) || null
                }

                onChange={(e, newValue) => {
                  setForm((prev) => ({
                    ...prev,
                    program_id: newValue?.id || "",
                  }));

                  if (errors.program_id) {
                    setErrors((prev) => ({ ...prev, program_id: "" }));
                  }
                }}

                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Program *"
                    size="small"
                    error={!!errors.program_id}
                    helperText={errors.program_id}
                  />
                )}
              />
            </div>

            {/* Start Date */}
            <div className="col-12 col-md-6">
              <DatePicker
                label="Start Date"
                value={form.start_date}
                onChange={handleDateChange("start_date")}
                format="dd MMM yyyy"
                minDate={new Date()}
                maxDate={form.end_date ?? undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </div>

            {/* End Date */}
            <div className="col-12 col-md-6">
              <DatePicker
                label="End Date"
                value={form.end_date}
                onChange={handleDateChange("end_date")}
                format="dd MMM yyyy"
                minDate={form.start_date ?? undefined}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </div>

          </div>

          {/* Employee Selector */}
          <div className="mt-4">
            <EmployeeSelector
              employees={employees}
              selectedEmployeeIds={form.employee_ids}
              onChange={handleEmployeeChange}
            />
            {errors.employee_ids && (
              <div className="text-danger small mt-1">{errors.employee_ids}</div>
            )}
          </div>

          {/* Footer */}
          <div className="d-flex justify-content-end gap-3 mt-4">
            <button
              className="btn btn-secondary px-4 fw-medium"
              onClick={() => setModalVisible(false)}
            >
              Cancel
            </button>

            <button
              className="btn btn-success px-4 fw-medium"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>

        </ModalWrapper>
      )}
      {viewModal && (
        <ModalWrapper onClose={() => setViewModal(false)}>

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="fw-bold mb-0 text-dark">Assigned Employees</h5>
            <IconButton size="small" onClick={() => setViewModal(false)} aria-label="close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          {/* Body */}
          <div style={{ maxHeight: 300, overflowY: "auto" }} className="p-1">
            {selectedEmployees.length === 0 ? (
              <p className="text-muted text-center mb-0">No employees assigned</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {selectedEmployees.map((emp) => (
                  <span
                    key={emp._UserId}
                    className="badge bg-success text-white px-3 py-2"
                    style={{ borderRadius: 20, fontSize: 13 }}
                  >
                    {emp.firstName} {emp.lastName}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="d-flex justify-content-center mt-4">
            <button
              className="btn btn-secondary px-4 fw-medium"
              onClick={() => setViewModal(false)}
            >
              Close
            </button>
          </div>

        </ModalWrapper>
      )}

    </div>
  );
};

export default ProgramAssignList;